'use client';

/**
 * The document viewer's BODY — toolbar, finder, legend, canvas, guidance rail
 * and all per-document state (page, zoom, finder, highlight, refresh) —
 * with no opinion about where it lives (spec 2026-10-08 D3: one viewer, two
 * hosts). The app-wide `DocumentViewerPanel` wraps it in the fixed, dockable,
 * resizable dialog; the freeform artifact tab renders it bare, filling the tab.
 *
 * Per-document state resets when the document's identity (`documentKey ??
 * pdfUrl`) changes, and survives a `refreshToken` change of the same document.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from 'react';
import dynamic from 'next/dynamic';
import { findMatches, type DocumentHighlight, type TextMatch } from './text-search';
import { locateAnchorPage } from './locate-anchor';
import { ViewerToolbar } from './viewer-toolbar';
import { PreviewLegend } from './preview-legend';
import { GuidanceRail } from './guidance-rail';
import { ZOOM_STEPS, type ViewerSource } from './types';

// pdf.js's core module (react-pdf -> pdfjs-dist/.../display/canvas.js) touches
// `DOMMatrix` at module-evaluation time, which does not exist in Node. This
// panel is mounted in the root providers tree, so a static import of
// `PdfCanvas` gets pulled into every page's server render and breaks `next
// build` (prerendering evaluates the module on the server). `ssr: false`
// keeps pdf.js out of the server bundle entirely — it only ever loads in
// the browser, which is the only place a document is ever opened anyway.
const PdfCanvas = dynamic(() => import('./pdf-canvas').then((m) => m.PdfCanvas), {
  ssr: false,
  loading: () => <p className="py-10 text-center text-xs text-muted">Loading document…</p>,
});

export interface ViewerRootProps {
  role?: string;
  'aria-modal'?: boolean;
  'aria-label'?: string;
  tabIndex?: number;
  'data-dock-style'?: string;
  'data-testid'?: string;
  style?: CSSProperties;
  className?: string;
}

export interface DocumentViewerViewProps {
  source: ViewerSource;
  /** The toolbar's × and Escape. Absent (an embedded host): neither. */
  onClose?: () => void;
  dockStyle?: 'overlay' | 'embedded';
  onToggleDockStyle?: () => void;
  locateRequest?: { needle: string; pdfUrl: string; nonce: number } | null;
  guidanceRail?: 'expanded' | 'collapsed';
  onToggleGuidanceRail?: () => void;
  /** The root element: the global panel is an <aside role="dialog">. */
  as?: 'div' | 'aside';
  rootRef?: Ref<HTMLElement>;
  /** Replaces the default root attributes (the embedded, fill-the-container layout). */
  rootProps?: ViewerRootProps;
  /** Rendered first inside the root — the global panel's resize handle. */
  leading?: ReactNode;
}

const EMBEDDED_ROOT: ViewerRootProps = {
  'data-testid': 'document-viewer-view',
  className: 'flex h-full min-h-0 w-full flex-col bg-surface',
};

export function DocumentViewerView({
  source,
  onClose,
  dockStyle,
  onToggleDockStyle,
  locateRequest = null,
  guidanceRail = 'expanded',
  onToggleGuidanceRail,
  as = 'div',
  rootRef,
  rootProps = EMBEDDED_ROOT,
  leading,
}: DocumentViewerViewProps) {
  const [page, setPage] = useState(1);
  // Incremented on every deliberate jump; see `jump` below.
  const [jumpSeq, setJumpSeq] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  // Multiple of fit-to-width. Per open: reset with the rest of the per-document
  // state below, deliberately not persisted.
  const [zoom, setZoom] = useState(1);
  const zoomIn = useCallback(
    () => setZoom((z) => ZOOM_STEPS.find((s) => s > z) ?? z),
    [],
  );
  const zoomOut = useCallback(
    () => setZoom((z) => [...ZOOM_STEPS].reverse().find((s) => s < z) ?? z),
    [],
  );
  const zoomFit = useCallback(() => setZoom(1), []);
  const [refreshing, setRefreshing] = useState(false);
  const [pageTexts, setPageTexts] = useState<string[]>([]);
  // The unjoined form of `pageTexts`: per-page text ITEMS, so a match can be
  // addressed to the span the canvas will mark. Both arrive from one
  // extraction pass in PdfCanvas.
  const [pageItems, setPageItems] = useState<string[][]>([]);
  // Finder state. `finderIndex` is into the matches memo below.
  const [finderQuery, setFinderQuery] = useState('');
  const [finderIndex, setFinderIndex] = useState(0);
  // What the canvas is marking right now — set by the finder OR by a
  // click-to-locate request; whichever spoke last wins, which is what a
  // reader expects.
  const [highlight, setHighlight] = useState<DocumentHighlight | null>(null);
  const finderInputRef = useRef<HTMLInputElement | null>(null);
  const lastToken = useRef(source?.refreshToken);
  // The identity of the document on screen. Never null here: the global
  // panel unmounts this view when it closes. A caller-supplied
  // `documentKey` wins over the URL (a source
  // whose revisions each live at a new URL); without one the URL is the
  // identity, as it always was.
  const documentKey = source.documentKey ?? source.pdfUrl;
  const lastKey = useRef<string | null>(documentKey);

  // ONE reset discipline for every piece of per-document state.
  //
  // This panel is mounted once for the life of the app and never unmounts,
  // so anything not reset here outlives the document it describes. Leaving
  // `pageCount` behind was visible: open a 37-page document, close it, open
  // another, and the toolbar reads "Page of 37" — with the jump input's
  // `max` still 37 — until the new document finishes loading; on a first-load
  // failure it stays wrong forever, and a jump accepted against that stale
  // `max` lands out of range in the new document. `refreshing` left behind
  // means a close during an update re-opens under a stuck overlay, and stale
  // `pageTexts` lets the guidance rail resolve anchors onto the wrong pages.
  //
  // Runs on document change AND on close (documentKey -> null).
  useEffect(() => {
    setPage(1);
    setZoom(1);
    setPageCount(0);
    setPageTexts([]);
    // A different document: its text, the finder's matches into it, and any
    // marks all belong to the old one.
    setPageItems([]);
    setFinderQuery('');
    setFinderIndex(0);
    setHighlight(null);
    setRefreshing(false);
  }, [documentKey]);

  // Re-clamp when the document underneath shrinks.
  //
  // `jump` clamps at call time against the `pageCount` known THEN; nothing
  // re-clamped when `pageCount` itself changed. A refresh that shrinks the
  // document (editing a field genuinely can) while `page` sits past the new
  // end leaves PdfCanvas's `visiblePages` window empty — "Page 9 of 4" over
  // a blank panel, recoverable only by retyping. Reachable today too, via
  // the stale `max` above.
  useEffect(() => {
    if (pageCount <= 0) return;
    setPage((prev) => (prev > pageCount ? pageCount : prev));
  }, [pageCount]);

  // Track refreshToken changes and show the updating state until the new
  // render lands (onPageCount) or the attempt fails (onLoadError). Gated on
  // the URL staying the SAME: this panel is mounted once, for the life of
  // the app, so the very first document ever opened — and every later open
  // of a different document — would otherwise also read as "the token
  // changed" (the ref starts at whatever the seed value was) and show this
  // overlay stacked on top of the canvas's own first-load state. Only a
  // same-document refresh is an "update"; a different document opening is a
  // first load.
  //
  // The refs are updated even when `source` is null, so a close-then-reopen
  // of the SAME document reads as a first load (prevUrl undefined) rather
  // than as a refresh — otherwise reopening after a refresh-while-closed
  // would paint the updating overlay over a genuine first load.
  //
  // Declared AFTER the reset effect above so that on a document change the
  // reset runs first and this one cannot be undone by it.
  useEffect(() => {
    const prevKey = lastKey.current;
    const prevToken = lastToken.current;
    lastKey.current = documentKey;
    lastToken.current = source?.refreshToken;
    if (prevKey === documentKey && prevToken !== source.refreshToken) {
      setRefreshing(true);
      // The extracted text describes the OUTGOING render. Holding onto it
      // across a refresh makes the rail offer confident jumps to page
      // numbers from a document that is being replaced — and if extraction
      // then throws, `onTextExtracted` never fires and those wrong numbers
      // stand indefinitely. Clearing degrades the rail to "listed, not
      // clickable", its documented graceful state.
      setPageTexts([]);
    }
  }, [source, documentKey]);

  // Escape closes — matches every other overlay in the app. Scoped to the
  // panel rather than `document`: this panel can be opened from inside
  // another dialog, and a document-level listener meant one Escape closed
  // BOTH. React's synthetic onKeyDown bubbles from whatever inside the panel
  // has focus, which — thanks to the focus handling below — is where focus
  // is while the panel is open.
  const onPanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl/⌘F inside the panel goes to OUR finder, not the browser's. The
      // browser's find only sees the five mounted pages, so on a long
      // document it would report a clause absent that is merely on page 30.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        e.stopPropagation();
        finderInputRef.current?.focus();
        finderInputRef.current?.select();
        return;
      }
      // Ctrl/⌘ + / - / 0 zoom the DOCUMENT; preventDefault stops the browser
      // zooming the whole page instead.
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          zoomIn();
          return;
        }
        if (e.key === '-') {
          e.preventDefault();
          zoomOut();
          return;
        }
        if (e.key === '0') {
          e.preventDefault();
          zoomFit();
          return;
        }
      }
      if (e.key !== 'Escape' || !onClose) return;
      e.stopPropagation();
      onClose();
    },
    [onClose, zoomIn, zoomOut, zoomFit],
  );

  // Clamped to [1, pageCount] here, in the one funnel every jump (typed,
  // Prev, Next) goes through — not just via the input's `max` attribute,
  // which is a UI hint a caseworker can type straight past. An unclamped
  // jump (e.g. typing 999 into a 5-page document) empties `visiblePages` in
  // PdfCanvas: a blank panel reading "Page 999 of 5" with no way back except
  // retyping. Before `pageCount` is known (still 0, mid-fetch) only the
  // lower bound applies — there is nothing to clamp the upper end to yet.
  const jump = useCallback((next: number) => {
    if (!Number.isFinite(next)) return;
    setPage((prev) => {
      if (!Number.isFinite(next)) return prev;
      const lowerBound = Math.max(1, next);
      return pageCount > 0 ? Math.min(lowerBound, pageCount) : lowerBound;
    });
    // Tells PdfCanvas this page change is a JUMP and should move the
    // viewport. Scroll-driven changes go through `onVisiblePageChange` and
    // must NOT bump this, or the canvas scrolls while the user is scrolling
    // and the document snaps page-to-page. A counter rather than the page
    // number so jumping to the page you are already on still scrolls (a
    // guidance anchor on the current page, say).
    setJumpSeq((n) => n + 1);
  }, [pageCount]);

  // ── Finder ───────────────────────────────────────────────────────────────
  // Matches are recomputed only when the query or the extracted text changes,
  // never on scroll (the panel re-renders on every visible-page tick).
  const matches = useMemo<TextMatch[]>(
    () => (finderQuery.trim() ? findMatches(pageItems, finderQuery) : []),
    [pageItems, finderQuery],
  );
  const goToMatch = useCallback(
    (index: number, needle: string, list: TextMatch[]) => {
      const m = list[index];
      if (!m) return;
      jump(m.page);
      setHighlight((prev) => ({ needle, current: m, nonce: (prev?.nonce ?? 0) + 1 }));
    },
    [jump],
  );
  const onFinderChange = useCallback((q: string) => {
    setFinderQuery(q);
    setFinderIndex(0);
  }, []);
  // A new query lands on its first match as soon as one exists; clearing the
  // query clears the marks. Keyed on `matches` (which already tracks the
  // query and the text) so typing feels live rather than Enter-gated.
  useEffect(() => {
    if (!finderQuery.trim()) {
      setHighlight((prev) => (prev && prev.needle === '' ? prev : null));
      return;
    }
    if (matches.length === 0) {
      setHighlight(null);
      return;
    }
    goToMatch(0, finderQuery, matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);
  const onFinderNext = useCallback(() => {
    if (matches.length === 0) return;
    const next = (finderIndex + 1) % matches.length;
    setFinderIndex(next);
    goToMatch(next, finderQuery, matches);
  }, [matches, finderIndex, finderQuery, goToMatch]);
  const onFinderPrev = useCallback(() => {
    if (matches.length === 0) return;
    const prev = (finderIndex - 1 + matches.length) % matches.length;
    setFinderIndex(prev);
    goToMatch(prev, finderQuery, matches);
  }, [matches, finderIndex, finderQuery, goToMatch]);
  const onFinderClear = useCallback(() => {
    setFinderQuery('');
    setFinderIndex(0);
    setHighlight(null);
  }, []);

  // ── Click-to-locate ──────────────────────────────────────────────────────
  // A request names the document it is about; one for a document this panel
  // is not showing is ignored (DocumentPreview opens it, and the request is
  // then honoured once the new document's text has been extracted — which is
  // why `pageItems` is a dependency and an unextracted document defers rather
  // than fails).
  const handledLocate = useRef(0);
  useEffect(() => {
    if (!locateRequest) return;
    if (locateRequest.pdfUrl !== source.pdfUrl) return;
    if (handledLocate.current === locateRequest.nonce) return;
    if (pageItems.length === 0) return; // text not extracted yet — wait
    handledLocate.current = locateRequest.nonce;

    const needle = locateRequest.needle;
    const found = findMatches(pageItems, needle);

    // Route THROUGH the finder, not around it. Clicking a name used to set the
    // highlight directly, which left the finder empty: the reader could see
    // one occurrence marked but had no count, and no way to step to the next
    // one. Putting the text in the finder gives them both, and means there is
    // one path that decides where the viewer goes instead of two that could
    // disagree.
    if (finderQuery !== needle) {
      // The finder effect above lands on match 0 once `matches` recomputes.
      setFinderQuery(needle);
      setFinderIndex(0);
    } else {
      // Same text again: `matches` will not change, so the effect will not
      // fire — but a repeat click is "take me there again", so go directly.
      setFinderIndex(0);
      goToMatch(0, needle, found);
    }

    // No exact span holds the text (it straddles two runs, or the PDF
    // hyphenated it). The finder will honestly read "No matches"; fall back to
    // the page-level paragraph match the guidance rail uses, so the reader at
    // least lands on the right page.
    if (found.length === 0) {
      const page = locateAnchorPage(needle, pageTexts);
      if (page) jump(page);
    }
  }, [locateRequest, source, pageItems, pageTexts, jump, finderQuery, goToMatch]);

  // `div` and `aside` render the same way here; the only typing difference is
  // the ref's element type (HTMLDivElement vs HTMLElement), which the host's
  // `Ref<HTMLElement>` covers for both.
  const Root = as;

  return (
    <Root ref={rootRef as Ref<HTMLDivElement>} {...rootProps} onKeyDown={onPanelKeyDown}>
      {leading}
      <ViewerToolbar
        source={source}
        page={page}
        pageCount={pageCount}
        onPrev={() => jump(page - 1)}
        onNext={() => jump(page + 1)}
        onJump={jump}
        onClose={onClose}
        dockStyle={dockStyle}
        onToggleDockStyle={onToggleDockStyle}
        zoom={zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomFit={zoomFit}
        finder={{
          query: finderQuery,
          matchCount: matches.length,
          matchIndex: finderIndex,
          onChange: onFinderChange,
          onNext: onFinderNext,
          onPrev: onFinderPrev,
          onClear: onFinderClear,
          inputRef: finderInputRef,
        }}
      />
      {/* The key sits ABOVE the scroll area, not inside it: it explains the
          highlights on every page, so scrolling away from it would be
          scrolling away from the legend for what you are looking at. */}
      {source.legend && (
        <div className="border-b border-line px-4 pb-2">
          <PreviewLegend variant={source.legend} />
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        <div className="relative flex-1 overflow-auto bg-stone-100 p-4">
          {refreshing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
              <p className="text-xs font-medium text-muted">Updating document…</p>
            </div>
          )}
          <PdfCanvas
            url={source.pdfUrl}
            documentKey={source.documentKey}
            refreshToken={source.refreshToken}
            page={page}
            jumpSeq={jumpSeq}
            onPageCount={(n) => { setPageCount(n); setRefreshing(false); }}
            // The overlay's only OTHER exit — without this, a refresh that
            // fails to load (network blip, corrupt bytes) leaves "Updating
            // document…" glued over the canvas's own error state forever;
            // this panel never unmounts, so reopening the same document
            // doesn't clear it either (Task 5 review Critical 1).
            describeLoadError={source.describeLoadError}
            onLoadError={(_message, info) => {
              setRefreshing(false);
              // The caller decides what a failure means for ITS document
              // (an expired specimen token is re-minted); the viewer only
              // reports which URL failed and how.
              source.onLoadError?.({ pdfUrl: info.url, documentKey: source.documentKey, status: info.status });
            }}
            onVisiblePageChange={setPage}
            onTextExtracted={setPageTexts}
            onTextItemsExtracted={setPageItems}
            highlight={highlight}
            zoom={zoom}
          />
        </div>
        {!!source.guidanceAnchors?.length && (
          <GuidanceRail
            anchors={source.guidanceAnchors}
            pageTexts={pageTexts}
            onJump={jump}
            collapsed={guidanceRail === 'collapsed'}
            onToggleCollapsed={() => onToggleGuidanceRail?.()}
          />
        )}
      </div>
    </Root>
  );
}
